/**
 * รับรองเวลาเข้างาน (มุมพนักงาน) — ขอแก้/รับรองเวลาเข้า-ออกงาน กรณีลืมตอก/ตอกผิด/
 * เน็ตหลุด (เพราะปุ่มเช็คอิน-เช็คเอาท์ถูกล็อกกันกดเบิ้ล). อนุมัติโดย HR เท่านั้น.
 */
import { useState } from 'react';
import { Clock, Send } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyAttendanceCorrections, useCreateAttendanceCorrection, type AttendanceCorrectionRow } from '../hooks';
import { useToast } from '../components/ui/toast';

const STATUS: Record<AttendanceCorrectionRow['status'], { label: string; cls: string }> = {
  pending: { label: 'รอ HR อนุมัติ', cls: 'badge-warn' },
  approved: { label: 'รับรองแล้ว', cls: 'badge-success' },
  rejected: { label: 'ไม่อนุมัติ', cls: 'badge-danger' },
  cancelled: { label: 'ยกเลิก', cls: 'badge-danger' },
};
const KIND_LABEL: Record<string, string> = { morning_in: 'เข้างาน (เช้า)', evening_out: 'เลิกงาน (เย็น)' };

function fmtThai(iso: string): string {
  const d = new Date(`${iso}T00:00:00+07:00`);
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(d);
}
function todayThai(): string {
  const nowThai = new Date(Date.now() + 7 * 3600_000);
  const t = new Date(Date.UTC(nowThai.getUTCFullYear(), nowThai.getUTCMonth(), nowThai.getUTCDate()));
  return t.toISOString().slice(0, 10);
}

export function AttendanceCorrection() {
  const mine = useMyAttendanceCorrections();
  const create = useCreateAttendanceCorrection();
  const toast = useToast();

  const maxDate = todayThai();   // รับรองย้อนหลังได้ ห้ามอนาคต
  const [date, setDate] = useState(maxDate);
  const [kind, setKind] = useState<'' | 'morning_in' | 'evening_out'>('');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');

  function submit() {
    if (!date) return toast.error('เลือกวันที่');
    if (!kind) return toast.error('เลือกรอบที่ขอรับรอง (เข้างาน หรือ เลิกงาน)');
    if (!time) return toast.error('ระบุเวลาที่ต้องการรับรอง');
    if (!reason.trim()) return toast.error('ต้องระบุเหตุผล');
    create.mutate(
      { date, kind, requested_time: time, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success('ส่งคำขอรับรองเวลาแล้ว — รอ HR อนุมัติ');
          setTime(''); setReason('');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ส่งคำขอไม่สำเร็จ'),
      },
    );
  }

  const rows = mine.data ?? [];

  return (
    <PageLayout title="รับรองเวลาเข้างาน">
      <div className="space-y-5 pb-10">
        <div className="card p-4 space-y-3">
          <div className="font-medium text-ink flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-teal" /> ขอรับรองเวลาเข้า-ออกงาน
          </div>
          <p className="text-xs text-gray-500">
            ใช้กรณี <b>ลืมตอก / ตอกผิดเวลา / เน็ตหลุด</b> — ระบุเวลาที่ถูกต้อง แล้วส่งให้ <b>HR</b> รับรอง.
            เมื่ออนุมัติ ระบบจะบันทึกเวลานี้เป็นเวลาเข้า/ออกงานของวันนั้น.
          </p>
          <div>
            <label className="label">วันที่</label>
            <input type="date" value={date} max={maxDate} onChange={(e) => setDate(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">รอบที่ขอรับรอง</label>
            <select value={kind} onChange={(e) => setKind(e.target.value as '' | 'morning_in' | 'evening_out')} className="input w-full">
              <option value="" disabled>เลือกรอบ…</option>
              <option value="morning_in">เข้างาน (เช้า)</option>
              <option value="evening_out">เลิกงาน (เย็น)</option>
            </select>
            <p className="mt-1 text-xs text-amber-700">ลืมสแกน<b>ออกงาน</b>? เลือก &ldquo;เลิกงาน (เย็น)&rdquo; — อย่าเลือกเข้างาน ไม่งั้นเวลาเข้าจะถูกทับเป็นตอนเย็น</p>
          </div>
          <div>
            <label className="label">เวลาที่ถูกต้อง</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">เหตุผล (จำเป็น)</label>
            <input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น ลืมตอกบัตรตอนเข้างาน" className="input w-full" maxLength={300}
            />
          </div>
          <button
            onClick={submit}
            disabled={create.isPending || !date || !time || !reason.trim()}
            className="btn-cta w-full justify-center flex items-center gap-2"
          >
            <Send className="w-4 h-4" /> {create.isPending ? 'กำลังส่ง…' : 'ส่งคำขอรับรองเวลา'}
          </button>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">คำขอของฉัน</div>
          {mine.isLoading ? (
            <p className="text-sm text-gray-400">กำลังโหลด…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400">ยังไม่มีคำขอ</p>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="card p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-ink">
                    {fmtThai(r.date)} · {KIND_LABEL[r.kind] ?? r.kind} · <b>{r.requested_time} น.</b>
                  </div>
                  <span className={`${STATUS[r.status].cls} shrink-0`}>{STATUS[r.status].label}</span>
                </div>
                <p className="text-xs text-gray-500">เหตุผล: {r.reason}</p>
                {r.status === 'rejected' && r.rejection_reason && (
                  <p className="text-xs text-red-600">เหตุผลที่ไม่อนุมัติ: {r.rejection_reason}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </PageLayout>
  );
}
